import { useState, useEffect, useCallback } from "react";
import { Pencil, PlusCircle, Trash2 } from "lucide-react";
import { ERROR_MESSAGE } from "../../constants";
import {
  getLLMConfigList,
  deleteLLMConfig,
  saveLLMConfig,
  updateLLMConfig,
} from "../../api/common";
import LoadingOverlay from "../../Components/LoadingOverlay";
import PageContainer from "../PageContainer";
import Alert from "../../Components/AlertPopup";
import AddLLMModal, { AddLLMFormData } from "./AddLLMModal";
import ConfirmBox from "../../Components/ConfirmBox";
import Breadcrumb from "../../Components/Breadcrumb";
import styles from "./style.module.scss";

// ----- Types -----
interface LLMConfigItem {
  id: number;
  type: string;
  providerName: string;
  providerType: string;
  modelName: string;
  endpoint: string;
  apiKey: string;
  temperature: number;
  maxTokens: number;
  dimensions?: number;
  region?: string;
  accessKey?: string;
  secretKey?: string;
  authType?: string;
}

const breadcrumbItems = [
  { label: "Configuration", path: "#" },
  { label: "Settings", path: "#" },
  { label: "LLM Configuration" },
];

const LLMConfig = () => {
  const [loading, setLoading] = useState(false);
  const [showErrorToast, setShowErrorToast] = useState(false);
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage] = useState(ERROR_MESSAGE.API_ERROR_MESSAGE);

  const [configs, setConfigs] = useState<LLMConfigItem[]>([]);
  const [activeTab, setActiveTab] = useState<"query" | "embedding">("query");
  const [selectedConfigId, setSelectedConfigId] = useState<number | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editFormData, setEditFormData] = useState<LLMConfigItem | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const fetchConfigs = useCallback(async (type: "query" | "embedding") => {
    setLoading(true);
    try {
      const apiType = type === "query" ? "QUERY" : "EMBEDDING";
      const data = await getLLMConfigList(apiType);
      setConfigs(data);
      setSelectedConfigId(data[0]?.id ?? null);
    } catch {
      setShowErrorToast(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConfigs(activeTab);
  }, [activeTab, fetchConfigs]);

  const filteredConfigs = configs;
  const selectedConfig =
    filteredConfigs.find((c) => c.id === selectedConfigId) ?? null;

  const handleCardSelect = (id: number) => {
    setSelectedConfigId(id);
    setIsEditMode(false);
    setEditFormData(null);
  };

  const handleEditClick = () => {
    if (!selectedConfig) return;
    setEditFormData({ ...selectedConfig });
    setIsEditMode(true);
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    setEditFormData(null);
  };

  const handleUpdateConfig = async () => {
    if (!editFormData) return;
    const isSelfHosted = editFormData.providerName === "Self-Hosted";
    const payload = {
      type: activeTab === "query" ? "QUERY" : "EMBEDDING",
      providerName: editFormData.providerName,
      providerType: isSelfHosted ? "Self-Hosted" : "Cloud-Managed",
      modelName: editFormData.modelName,
      endpoint: editFormData.endpoint || null,
      region: isSelfHosted ? null : editFormData.region,
      temperature: editFormData.temperature,
      maxTokens: editFormData.maxTokens,
      dimensions: activeTab === "embedding" ? editFormData.dimensions : null,
      authType: isSelfHosted ? "Bearer" : "AWS",
      accessKey: isSelfHosted ? null : editFormData.accessKey,
      secretKey: isSelfHosted ? null : editFormData.secretKey,
      apiKey: isSelfHosted ? editFormData.apiKey : null,
    };

    setLoading(true);
    try {
      await updateLLMConfig(editFormData.id, payload);
      setIsEditMode(false);
      setEditFormData(null);
      await fetchConfigs(activeTab);
      setSuccessMessage("Configuration updated successfully!");
      setShowSuccessToast(true);
    } catch {
      setShowErrorToast(true);
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (
    field: keyof LLMConfigItem,
    value: string | number,
  ) => {
    if (!editFormData) return;
    setEditFormData({ ...editFormData, [field]: value });
  };

  const handleDeleteConfig = async () => {
    if (!selectedConfigId) return;
    setLoading(true);
    try {
      await deleteLLMConfig(selectedConfigId);
      setShowDeleteConfirm(false);
      await fetchConfigs(activeTab);
      setSuccessMessage("Configuration deleted successfully!");
      setShowSuccessToast(true);
    } catch {
      setShowErrorToast(true);
      setShowDeleteConfirm(false);
    } finally {
      setLoading(false);
    }
  };

  const handleErrorClose = () => setShowErrorToast(false);

  const handleAddLLM = async (data: AddLLMFormData) => {
    const isSelfHosted = data.provider === "self-hosted";
    const payload = {
      type: activeTab === "query" ? "QUERY" : "EMBEDDING",
      providerName: isSelfHosted ? "Self-Hosted" : "AWS Bedrock",
      providerType: isSelfHosted ? "Self-Hosted" : "Cloud-Managed",
      modelName: data.modelName,
      endpoint: data.endpointUrl || null,
      region: isSelfHosted ? null : data.region,
      temperature: data.temperature,
      maxTokens: data.maxTokens,
      dimensions: activeTab === "embedding" ? data.dimension : null,
      authType: isSelfHosted ? "Bearer" : "AWS",
      accessKey: isSelfHosted ? null : data.accessKeyId,
      secretKey: isSelfHosted ? null : data.secretAccessKey,
      apiKey: isSelfHosted ? data.apiKey : null,
    };

    setLoading(true);
    try {
      await saveLLMConfig(payload);
      setShowAddModal(false);
      await fetchConfigs(activeTab);
      setSuccessMessage("Configuration added successfully!");
      setShowSuccessToast(true);
    } catch {
      setShowErrorToast(true);
    } finally {
      setLoading(false);
    }
  };

  const getBadgeClass = (providerType: string) =>
    providerType === "Self-Hosted" ? styles.selfHostedBadge : styles.cloudManagedBadge;

  const renderField = (
    label: string,
    field: keyof LLMConfigItem,
    type: "text" | "number" = "text",
    placeholder?: string,
  ) => {
    const value =
      isEditMode && editFormData ? editFormData[field] : selectedConfig?.[field];

    // Mask sensitive fields in view mode with stars
    let displayValue: string | null = value ? String(value) : null;
    if (!isEditMode && (field === "apiKey" || field === "accessKey" || field === "secretKey") && displayValue) {
      const maxStars = field === "apiKey" ? 90 : 30;
      displayValue = "*".repeat(Math.min(displayValue.length, maxStars));
    }

    return (
      <div className={styles.fieldGroup}>
        <div className={styles.fieldLabel}>{label}</div>
        {isEditMode && field !== "providerName" ? (
          (field === "apiKey" || field === "accessKey" || field === "secretKey") ? (
            <textarea
              className={styles.textareaInput}
              value={value ?? ""}
              placeholder={placeholder}
              rows={1}
              ref={(el) => {
                if (el) {
                  el.style.height = "auto";
                  el.style.height = el.scrollHeight + "px";
                }
              }}
              onChange={(e) => {
                handleFieldChange(field, e.target.value);
                e.target.style.height = "auto";
                e.target.style.height = e.target.scrollHeight + "px";
              }}
            />
          ) : (
            <input
              className={type === "number" ? styles.numberInput : styles.fieldInput}
              type={type}
              value={value ?? ""}
              placeholder={placeholder}
              onChange={(e) =>
                handleFieldChange(
                  field,
                  type === "number" ? parseFloat(e.target.value) || 0 : e.target.value
                )
              }
            />
          )
        ) : (
          <div className={styles.fieldValue}>
            {displayValue ?? <span className={styles.fieldPlaceholder}>{placeholder ?? ""}</span>}
          </div>
        )}
      </div>
    );
  };

  return (
    <PageContainer>
      <div className={styles.breadcrumbNoNav}>
        <Breadcrumb items={breadcrumbItems} />
      </div>
      <LoadingOverlay loading={loading} />

      {/* Top bar: Tabs + Add LLM */}
      <div className={styles.topBar}>
        <div className={styles.tabToggle}>
          <button
            className={`${styles.tabBtn} ${activeTab === "query" ? styles.active : ""}`}
            onClick={() => {
              setActiveTab("query");
              setIsEditMode(false);
              setEditFormData(null);
            }}
          >
            Query Model
          </button>
          <button
            className={`${styles.tabBtn} ${activeTab === "embedding" ? styles.active : ""}`}
            onClick={() => {
              setActiveTab("embedding");
              setIsEditMode(false);
              setEditFormData(null);
            }}
          >
            Embedding Model
          </button>
        </div>
        <button className={styles.addBtn} onClick={() => setShowAddModal(true)}>
          <PlusCircle size={18} /> Add LLM
        </button>
      </div>

      {/* ===== GLOBAL CONTAINER ===== */}
      <div className={styles.globalContainer}>
        {/* ----- LEFT PANEL: Model Cards ----- */}
        <div className={styles.leftPanel}>
          {filteredConfigs.map((config) => (
            <div
              key={config.id}
              className={`${styles.configCard} ${selectedConfigId === config.id ? styles.selected : ""
                }`}
              onClick={() => handleCardSelect(config.id)}
            >
              {/* Card Header: Badge + Selected Dot */}
              <div className={styles.cardHeader}>
                <span
                  className={`${styles.cardBadge} ${getBadgeClass(config.providerType)}`}
                >
                  {config.providerType}
                </span>
                {selectedConfigId === config.id && (
                  <span className={styles.selectedDot} />
                )}
              </div>

              {/* Card Inner Details Container */}
              <div className={styles.cardDetails}>
                <div className={styles.cardRow}>
                  <span className={styles.cardLabel}>Provider:</span>
                  <span className={styles.cardValue}>
                    {config.providerName}
                  </span>
                </div>
                <div className={styles.cardRow}>
                  <span className={styles.cardLabel}>Model Name:</span>
                  <span className={styles.cardValue}>{config.modelName}</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* ----- RIGHT PANEL ----- */}
        <div className={styles.rightPanel}>
          <div className={styles.rightPanel2}>
            {selectedConfig ? (
              <>
                {/* Edit + Delete buttons — top-right, outside detail container */}
                {!isEditMode && (
                  <div className={styles.topActions}>
                    <button
                      className={styles.editBtn}
                      onClick={handleEditClick}
                    >
                      <Pencil size={14} /> Edit
                    </button>
                    <button
                      className={styles.deleteBtn}
                      disabled={isEditMode}
                      onClick={() => setShowDeleteConfirm(true)}
                    >
                      <Trash2 size={14} /> Delete
                    </button>
                  </div>
                )}

                {/* Detail Content Container (own border) */}
                <div className={styles.detailContainer}>
                  <div className={styles.subPanel}>
                    {/* Badge */}
                    <div className={styles.detailBadge}>
                      <span
                        className={`${styles.cardBadge} ${getBadgeClass(selectedConfig.providerType)}`}
                      >
                        {selectedConfig.providerType}
                      </span>
                    </div>

                    {selectedConfig.providerName === "Self-Hosted" ? (
                      <>
                        <div className={styles.providerPanel}>
                          {renderField("Provider", "providerName")}
                        </div>
                        <div className={styles.modelPanel}>
                          {/* Model Name + Endpoint URL */}
                          <div className={styles.fieldRow}>
                            {renderField("Model Name", "modelName")}
                            {renderField("Endpoint URL", "endpoint", "text", "https://api.example.com/v1")}
                          </div>
                          {/* API Key + Dimension */}
                          <div className={styles.fieldRow}>
                            {renderField("API Key", "apiKey", "text", "Enter API Key")}
                            {activeTab === "embedding" ? (
                              renderField("Dimension", "dimensions", "number")
                            ) : (
                              <div />
                            )}
                          </div>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className={styles.providerPanel}>
                          {/* Provider + Endpoint URL */}
                          <div className={styles.fieldRow}>
                            {renderField("Provider", "providerName")}
                            {renderField("Endpoint URL", "endpoint", "text", "https://api.example.com/v1")}
                          </div>
                        </div>
                        <div className={styles.modelPanel}>
                          {/* AWS Credentials */}
                          <div className={styles.sectionTitle}>AWS Credentials</div>
                          {/* Model Name + Region */}
                          <div className={styles.fieldRow}>
                            {renderField("Model Name", "modelName")}
                            {renderField("Region", "region", "text", "us-west-2a")}
                          </div>
                          {/* Access Key ID + Secret Access Key */}
                          <div className={styles.fieldRow}>
                            {renderField("Access Key ID", "accessKey", "text", "Enter Access Key ID")}
                            {renderField("Secret Access Key", "secretKey", "text", "Enter Secret Access Key")}
                          </div>
                          {activeTab === "embedding" && (
                            <div className={styles.fieldRow}>
                              {renderField("Dimension", "dimensions", "number")}
                              <div />
                            </div>
                          )}
                        </div>
                      </>
                    )}

                    {/* Generation Controls — own bordered section */}
                    <div className={styles.generationControls}>
                      <div className={styles.sectionTitle}>
                        Generation Controls
                      </div>
                      <div className={styles.fieldRow}>
                        {renderField("Temperature", "temperature", "number")}
                        {renderField("Maximum Tokens", "maxTokens", "number")}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Action buttons — edit mode only, outside detail container */}
                {isEditMode && (
                  <div className={styles.actionBar}>
                    <button
                      className={styles.cancelBtn}
                      onClick={handleCancelEdit}
                    >
                      Cancel
                    </button>
                    <button
                      className={styles.saveBtn}
                      onClick={handleUpdateConfig}
                    >
                      Update
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className={styles.emptyPanel}>
                Select a configuration to view details
              </div>
            )}
          </div>
        </div>
      </div>

      <AddLLMModal
        show={showAddModal}
        category={activeTab}
        onClose={() => setShowAddModal(false)}
        onSave={handleAddLLM}
      />

      <ConfirmBox
        show={showDeleteConfirm}
        message="Are you sure you want to delete this configuration?"
        onConfirm={handleDeleteConfig}
        handleClose={() => setShowDeleteConfirm(false)}
      />

      <Alert
        type="success"
        show={showSuccessToast}
        onClose={() => setShowSuccessToast(false)}
        message={successMessage}
      />

      <Alert
        type="error"
        show={showErrorToast}
        onClose={handleErrorClose}
        message={errorMessage}
      />
    </PageContainer>
  );
};

export default LLMConfig;
